'use client';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  selectProductionPeriodCreating,
  selectProductionPeriodData,
  selectProductionPeriodLoading,
  selectProductionPeriodError,
  selectProductionPeriodCreateError,
  selectProductionPeriodUpdating,
  selectProductionPeriodUpdateError,
  selectProductionPeriodDeleting,
  selectProductionPeriodDeleteError,
} from '../../store/production-period.selectors';
import SelectProductionPeriod from './SelectProductionPeriod';
import AddProductionReportingListHeader from './header';
import AddProductionReportingListTable from './table';
import { useNotification } from '@/components/Notification/useNotification';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { getOutputMaterialList } from '@/features/match-maker/store/match-maker.thunk';
import { selectOutputMaterialsData } from '@/features/match-maker/store/match-maker.selectors';
import { MaterialFilterEnum } from '@/features/match-maker/utils/match-maker.enums';
import { formatDateInputValue, replacePlaceholders } from '@/utils/helpers';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import {
  getProductionPeriods,
  postProductionPeriod,
  updateProductionPeriodById,
  deleteProductionPeriodById,
} from '../../store/production-period.thunk';
import appLocalStorage from '@/features/session/store/appLocalStorage';
import { DeleteModal } from './DeleteModal';
import type {
  AddProductionPeriodFormSubmitPayload,
  EditProductionPeriodFormSubmitPayload,
  ProductionPeriod,
  ProductionPeriodFormSubmitPayload,
} from '../../utils/types';
import routes from '@/app/routes';
import {
  selectFacilityByIdData,
  selectFacilityByIdLoading,
} from '../../store/facility.selector';
import { getFacilityById } from '../../store/facility.thunk';
import { MINUTES_IN_A_DAY } from '../../utils/constants';
import { findTotalMinutesOfProductionPeriods } from '../../utils/helpers';

const AddProductionReportingListWrapper = () => {
  const dispatch = useAppDispatch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProductionPeriod, setSelectedProductionPeriod] =
    useState<ProductionPeriod | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteProductionPeriodId, setDeleteProductionPeriodId] = useState<
    string | null
  >(null);
  const outputMaterialsData = useAppSelector(selectOutputMaterialsData);
  const productionPeriodData = useAppSelector(selectProductionPeriodData);
  const isLoading = useAppSelector(selectProductionPeriodLoading);
  const isCreating = useAppSelector(selectProductionPeriodCreating);
  const error = useAppSelector(selectProductionPeriodError);
  const createError = useAppSelector(selectProductionPeriodCreateError);
  const isUpdating = useAppSelector(selectProductionPeriodUpdating);
  const updateError = useAppSelector(selectProductionPeriodUpdateError);
  const isDeleting = useAppSelector(selectProductionPeriodDeleting);
  const deleteError = useAppSelector(selectProductionPeriodDeleteError);
  const showNotification = useNotification();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { lineId } = useParams<{ lineId: string }>();
  const selectedDate =
    searchParams.get('date') ?? formatDateInputValue(new Date());

  const outputMaterialItems = useMemo(
    () => outputMaterialsData?.items ?? [],
    [outputMaterialsData]
  );
  const productionPeriodItems = useMemo(
    () => productionPeriodData?.items ?? [],
    [productionPeriodData]
  );

  const facilityDetail = useAppSelector(selectFacilityByIdData);
  const facilityDetailLoading = useAppSelector(selectFacilityByIdLoading);

  useEffect(() => {
    if (lineId) {
      dispatch(getFacilityById({ facilityId: lineId }));
    }
  }, [dispatch, lineId]);

  useEffect(() => {
    if (!facilityDetail) return;
    dispatch(
      getOutputMaterialList({
        sort_field: MaterialFilterEnum.DESCRIPTION,
        sort_order: MaterialFilterEnum.SORT_ASC,
        facility_code: facilityDetail.facility_code,
      })
    );
  }, [dispatch, facilityDetail]);

  useEffect(() => {
    const plant = appLocalStorage.getProductionUnit();

    if (!plant || !lineId) return;
    dispatch(
      getProductionPeriods({
        plant,
        facility_id: lineId,
        date: selectedDate,
      })
    );
  }, [dispatch, lineId, selectedDate]);

  useEffect(() => {
    if (error) {
      showNotification(error, 'destructive');
    }
  }, [error, showNotification]);

  useEffect(() => {
    if (createError) {
      showNotification(createError, 'destructive');
    }
  }, [createError, showNotification]);

  useEffect(() => {
    if (updateError) {
      showNotification(updateError, 'destructive');
    }
  }, [showNotification, updateError]);

  useEffect(() => {
    if (deleteError) {
      showNotification(deleteError, 'destructive');
    }
  }, [deleteError, showNotification]);

  const handleCreateProductionPeriod = useCallback(
    (data: AddProductionPeriodFormSubmitPayload) => {
      const plantCode = appLocalStorage.getProductionUnit();
      if (!plantCode || !lineId) return;
      dispatch(
        postProductionPeriod({
          plant: plantCode,
          facility_id: lineId,
          material_code: data.materialId,
          date: selectedDate,
          is_24_hour_production: false,
          start_time: data.startTime,
          end_time: data.endTime,
        })
      )
        .unwrap()
        .then(() => {
          showNotification('Production period added', 'success');
          setIsDrawerOpen(false);
          dispatch(
            getProductionPeriods({
              plant: plantCode,
              facility_id: lineId,
              date: selectedDate,
            })
          );
        });
    },
    [dispatch, lineId, selectedDate, showNotification]
  );

  const handleDeleteProductionPeriod = useCallback(
    (id: string) => {
      setIsDeleteModalOpen(true);
      setDeleteProductionPeriodId(id);
    },
    [setIsDeleteModalOpen, setDeleteProductionPeriodId]
  );

  const onConfirmDeleteProductionPeriod = () => {
    if (!deleteProductionPeriodId) return;
    dispatch(deleteProductionPeriodById(deleteProductionPeriodId!)).then(() => {
      showNotification('Production period deleted', 'success');
    });
    setIsDeleteModalOpen(false);
    setDeleteProductionPeriodId(null);
  };

  const onCancelDeleteProductionPeriod = () => {
    setIsDeleteModalOpen(false);
    setDeleteProductionPeriodId(null);
  };

  const handleReportProductionPeriod = useCallback(
    (pp: ProductionPeriod) => {
      navigate(
        replacePlaceholders(
          `${routes.PRODUCTION_REPORTING_REVIEW_PERIODS}?${searchParams.toString()}`,
          {
            lineId: lineId || '',
            pid: pp.id,
            materialId: pp.material_id,
          }
        )
      );
    },
    [lineId, navigate, searchParams]
  );

  const handleUpdateProductionPeriod = useCallback(
    (data: EditProductionPeriodFormSubmitPayload) => {
      if (!selectedProductionPeriod) return;

      const plantCode = appLocalStorage.getProductionUnit();
      if (!plantCode || !lineId) return;

      dispatch(
        updateProductionPeriodById({
          id: selectedProductionPeriod.id,
          body: {
            is_24_hour_production: Boolean(data.is_24_hour_production),
            start_time: data.startTime,
            end_time: data.endTime,
          },
        })
      )
        .unwrap()
        .then(() => {
          showNotification('Production period updated', 'success');
          setIsDrawerOpen(false);
          setSelectedProductionPeriod(null);
          dispatch(
            getProductionPeriods({
              plant: plantCode,
              facility_id: lineId,
              date: selectedDate,
            })
          );
        });
    },
    [dispatch, lineId, selectedDate, selectedProductionPeriod, showNotification]
  );

  const handleSelectProductionPeriod = useCallback(
    (data: ProductionPeriodFormSubmitPayload) => {
      if (selectedProductionPeriod) {
        handleUpdateProductionPeriod({
          startTime: data.startTime,
          endTime: data.endTime,
          is_24_hour_production: data.is_24_hour_production,
        });
        return;
      }

      handleCreateProductionPeriod(
        data as AddProductionPeriodFormSubmitPayload
      );
    },
    [
      handleCreateProductionPeriod,
      handleUpdateProductionPeriod,
      selectedProductionPeriod,
    ]
  );

  const handleOpenProductionPeriod = useCallback(() => {
    setSelectedProductionPeriod(null);
    setIsDrawerOpen(true);
  }, []);

  const handleEditProductionPeriod = useCallback(
    (id: string) => {
      const selectedPeriod =
        productionPeriodItems.find(period => period.id === id) ?? null;
      setSelectedProductionPeriod(selectedPeriod);
      setIsDrawerOpen(true);
    },
    [productionPeriodItems]
  );

  const completedSteps = useMemo(() => {
    if (!productionPeriodItems || productionPeriodItems.length === 0) return 0;
    const totalMinutes = findTotalMinutesOfProductionPeriods(
      productionPeriodItems
    );
    return totalMinutes > MINUTES_IN_A_DAY ? MINUTES_IN_A_DAY : totalMinutes;
  }, [productionPeriodItems]);

  return (
    <div className="flex flex-col min-h-[calc(100vh-150px)]">
      <div className="px-6 py-6 md:px-14 flex flex-col flex-1 gap-6">
        <AddProductionReportingListHeader
          openProductionPeriod={handleOpenProductionPeriod}
          selectedDate={selectedDate}
          facility={facilityDetail}
          isLoading={facilityDetailLoading}
          completedSteps={completedSteps}
        />
        <AddProductionReportingListTable
          list={productionPeriodItems}
          isLoading={isLoading}
          error={error}
          isDeleting={isDeleting}
          onDeletePeriod={handleDeleteProductionPeriod}
          onReportPeriod={handleReportProductionPeriod}
          onEditPeriod={handleEditProductionPeriod}
        />
        <SelectProductionPeriod
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          selectedProductionPeriod={selectedProductionPeriod}
          onSelectProductionPeriod={handleSelectProductionPeriod}
          outputMaterialList={outputMaterialItems}
          isCreating={selectedProductionPeriod ? isUpdating : isCreating}
        />
        <DeleteModal
          open={isDeleteModalOpen}
          isDeleting={isDeleting}
          onDelete={onConfirmDeleteProductionPeriod}
          onCancel={onCancelDeleteProductionPeriod}
        />
      </div>
    </div>
  );
};

export default AddProductionReportingListWrapper;
